import type { CameraShiftStatus } from "../types/cameraApi";

/**
 * 카메라 화각 status → Tailwind 클래스 + 한국어 라벨 매핑.
 *
 * 기존 `statusStyles.ts` 패턴을 본떴다. 기존 의미 색토큰(`bg-status-*`)을
 * **그대로 재사용**한다 (neue 토큰 추가 금지). 계약 §6 매핑:
 *
 *   NORMAL   → operational(녹) "정상"
 *   DETECTED → majorOutage(적) "화각 틀어짐"
 *   ERROR    → unknown(회)     "점검 필요"
 *   INIT     → degraded(황)    "기준 설정"
 *
 * 접근성: 호출 측은 항상 라벨도 함께 렌더해야 한다 — 색만으로는 부족
 * (status.claude.com 과 동일 규칙).
 */

interface ToneClasses {
  /** Solid swatch background (dots, history 미니 셀). */
  solid: string;
  /** Tinted background for banners/badges. */
  bg: string;
  /** Foreground text color matching `bg`. */
  fg: string;
  /** Subtle border color matching the tone. */
  border: string;
}

export interface CameraStatusStyle extends ToneClasses {
  label: string;
}

export const CAMERA_STATUS_STYLES: Record<
  CameraShiftStatus,
  CameraStatusStyle
> = {
  NORMAL: {
    label: "정상",
    solid: "bg-status-operational dark:bg-status-operational-dark",
    bg: "bg-status-operational-bg dark:bg-status-operational-darkBg",
    fg: "text-status-operational-fg dark:text-status-operational-darkFg",
    border:
      "border-status-operational/30 dark:border-status-operational-dark/30",
  },
  DETECTED: {
    label: "화각 틀어짐",
    solid: "bg-status-majorOutage dark:bg-status-majorOutage-dark",
    bg: "bg-status-majorOutage-bg dark:bg-status-majorOutage-darkBg",
    fg: "text-status-majorOutage-fg dark:text-status-majorOutage-darkFg",
    border:
      "border-status-majorOutage/30 dark:border-status-majorOutage-dark/30",
  },
  INIT: {
    label: "기준 설정",
    solid: "bg-status-degraded dark:bg-status-degraded-dark",
    bg: "bg-status-degraded-bg dark:bg-status-degraded-darkBg",
    fg: "text-status-degraded-fg dark:text-status-degraded-darkFg",
    border: "border-status-degraded/30 dark:border-status-degraded-dark/30",
  },
  ERROR: {
    label: "점검 필요",
    solid: "bg-status-unknown dark:bg-status-unknown-dark",
    bg: "bg-status-unknown-bg dark:bg-status-unknown-darkBg",
    fg: "text-status-unknown-fg dark:text-status-unknown-darkFg",
    border: "border-status-unknown/30 dark:border-status-unknown-dark/30",
  },
};

/** Defensive lookup — 서버가 새 enum 값을 추가해도 unknown(회색)으로 폴백한다. */
export function cameraStyle(
  status: CameraShiftStatus | string,
): CameraStatusStyle {
  return (
    CAMERA_STATUS_STYLES[status as CameraShiftStatus] ??
    CAMERA_STATUS_STYLES.ERROR
  );
}
